// Shared theme resolver (ADR 0097). Dark mode is driven by data-theme="dark" on <html>; these helpers
// resolve the effective mode from a preference (following the OS for `system`) and apply/observe it.
// grow-mobile layers its persisted `system|light|dark` setting on top of these primitives; surfaces that
// always follow the OS (the Accounts App, the storefront) use `followSystemTheme()`.

export type ThemePref = 'system' | 'light' | 'dark'
export const THEME_PREFS: ThemePref[] = ['system', 'light', 'dark']

export function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )
}

/** Resolve a preference to the effective dark boolean (`system` follows the OS). */
export function resolveDark(pref: ThemePref): boolean {
  return pref === 'dark' ? true : pref === 'light' ? false : systemPrefersDark()
}

/** Apply the effective mode to <html> — the single place data-theme is toggled. */
export function applyTheme(dark: boolean): void {
  if (typeof document === 'undefined') return
  if (dark) document.documentElement.setAttribute('data-theme', 'dark')
  else document.documentElement.removeAttribute('data-theme')
}

/** Subscribe to OS light/dark changes; the callback fires with the new system-dark boolean. Returns an
 *  unsubscribe. No-op (returns a no-op cleanup) where matchMedia is unavailable (SSR/tests). */
export function watchSystemTheme(onChange: (dark: boolean) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = () => onChange(mq.matches)
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}

/** For surfaces that always follow the OS: apply the current OS mode now and on every OS change.
 *  Returns an unsubscribe. */
export function followSystemTheme(): () => void {
  applyTheme(systemPrefersDark())
  return watchSystemTheme(applyTheme)
}
